"use client";

import React, { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, X, Reply } from '@/components/icons';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Session } from '@supabase/supabase-js';
import CommentForm from './CommentForm';

interface CommentItemProps {
  comment: {
    id: string;
    created_at: string;
    content: string;
    user_id: string;
    parent_id?: string | null;
    profiles: {
      username: string;
      avatar_url: string | null;
    } | null;
  };
  highlight?: boolean;
  currentUserId: string | undefined;
  noteAuthorId: string;
  onDeleteComment: (commentId: string) => Promise<void>;
  // Recursion support
  children?: React.ReactNode;
  noteId: string;
  noteAuthorUsername: string;
  onCommentAdded: () => void;
  onReply?: (commentId: string, username: string) => void;
  level?: number;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  highlight,
  currentUserId,
  noteAuthorId,
  onDeleteComment,
  children,
  noteId,
  noteAuthorUsername,
  onCommentAdded,
  onReply,
  level = 0,
}) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState(level === 0); // Expand top-level, collapse replies

  
  const username = comment.profiles?.username || 'Usuario Desconocido';
  const avatarUrl = comment.profiles?.avatar_url;

  // Determinar si el usuario actual tiene permiso para eliminar este comentario
  const canDelete = currentUserId && (currentUserId === comment.user_id || currentUserId === noteAuthorId);

  // Contar respuestas (children) para mostrar el botón
  const hasReplies = !!children && React.Children.count(children) > 0;

  const handleDeleteClick = () => {
    // Eliminación optimista - eliminar inmediatamente sin esperar respuesta
    setIsConfirmingDelete(false);
    // Llamar a onDeleteComment sin esperar (fire and forget)
    onDeleteComment(comment.id).catch((error) => {
      console.error("Error deleting comment:", error);
    });
  };

  const toggleReply = () => {
    if (onReply) {
      onReply(comment.id, username);
    }
  };

  // Si está confirmando eliminación, mostrar caja de confirmación
  if (isConfirmingDelete) {
    return (
      <div
        id={`comment-${comment.id}`}
        className="flex flex-col md:flex-row md:items-center gap-3 p-3 rounded-md bg-destructive/10 border border-destructive/30"
        style={{ marginLeft: `${Math.min(level * 1.5, 3)}rem` }}
      >
        <div className="flex-1">
          <p className="text-sm font-semibold text-foreground mb-1">¿Eliminar comentario?</p>
          <p className="text-xs text-muted-foreground">Esta acción no se puede deshacer.</p>
        </div>
        <div className="flex gap-2 flex-shrink-0 w-full md:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsConfirmingDelete(false)}
            className="text-xs flex-1 md:flex-none border-0"
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDeleteClick}
            className="text-xs flex-1 md:flex-none border-0 confirm-delete-btn !bg-red-600 !text-white"
          >
            Eliminar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        id={`comment-${comment.id}`}
        className={cn(
          "flex gap-3 p-2 rounded-md bg-muted/30 transition-all duration-500",
          highlight && "highlight-comment"
        )}
      >
        <div className="flex-shrink-0">
          <Link to={`/${username}`}>
            <Avatar className="h-8 w-8">
              <AvatarImage src={avatarUrl || undefined} alt={username} />
              <AvatarFallback>
                {username.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </Link>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col gap-0.5">
            <div>
              <Link to={`/${username}`} className="font-semibold text-sm hover:underline">
                @{username}
              </Link>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: es })}
              </span>
            </div>
          </div>
          <p className="text-sm text-foreground mt-1 break-words">{comment.content}</p>
          
          <div className="flex items-center gap-3 mt-2">
            <button 
              onClick={toggleReply}
              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors cursor-pointer"
              type="button"
            >
              <Reply className="h-3 w-3" />
              Responder
            </button>
          </div>
        </div>
        {canDelete && (
          <div className="flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 border-0 text-destructive button-destructive-ghost transition-colors"
              onClick={() => setIsConfirmingDelete(true)}
              aria-label="Eliminar comentario"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>

      {children && (
        <div className="flex flex-col gap-3 mt-2">
          {hasReplies && !expandedReplies && (
            <button
              onClick={() => setExpandedReplies(true)}
              className="text-xs text-primary hover:text-primary/80 font-medium transition-colors text-left"
            >
              Ver respuestas
            </button>
          )}
          {expandedReplies && (
            <>
              {children}
              {hasReplies && level > 0 && (
                <button
                  onClick={() => setExpandedReplies(false)}
                  className="text-xs text-muted-foreground hover:text-primary transition-colors text-left"
                >
                  Ocultar respuestas
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentItem;